import { all, call, delay, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { toast } from "react-toastify";
import queryString from "query-string";
import { getErrorMessage } from "utils/sagasErrorHandler";
import { configCiTestCaseStatuses } from "modules/ConfigCI/constants";
import { ITEMS_PER_PAGE } from "constants/general";
import {
    acceptPublishRequest,
    configCiTestCaseTaskCancel,
    getPublishRequest,
    getPublishRequestError,
    getPublishRequestSuccess,
    getTestCases,
    mergeAcceptPublishRequestDialog,
    mergeTestCases,
    rejectPublishRequest,
    setAcceptPublishRequestDialog,
    setTestTasks
} from "modules/ConfigCI/services/PublishRequests/actions";
import {
    acceptPublishRequestRequest,
    configCiTestCaseTaskCancelRequest,
    getConfigCiTestTasksRequest,
    getPublishRequestsRequest,
    getTestCaseRequest,
    publishRequestRejectRequest
} from "modules/ConfigCI/services/PublishRequests/api";
import { publishRequestsSelector } from "modules/ConfigCI/services/PublishRequests/selectors";

//Workers
function* handleRefreshPublishRequests() {
    const { search } = window.location;

    yield put(getPublishRequest({ limit: ITEMS_PER_PAGE, ...queryString.parse(search) }));
}

function* handleGetTestCase ({ id, body }) {
    const testCase = yield all(body.map(({ id }) => call(getTestCaseRequest, id)));

    yield put(mergeTestCases({ [id]: testCase }));

    const isCheckMore = testCase.some((item) => item.some(({ status_code }) => (
        status_code === configCiTestCaseStatuses.new || status_code === configCiTestCaseStatuses.inProgress))
    );

    if(isCheckMore){
        yield delay(4000);
        yield call(handleGetTestCase, { id, body });
    }
}

function* handleGetTestCasesData ({ payload }) {
    const parentIDs = Object.keys(payload);

    yield all(parentIDs.map((id) => call(handleGetTestCase, { id, body: payload[id] })));
}

function* handleGetPublishRequest ({ payload }) {
    try {
        const { publish_requests, count } = yield call(getPublishRequestsRequest, payload);
        const data = [ ...publish_requests ];

        const configCiTestTasks = yield all(publish_requests.map(({ id }) => call(getConfigCiTestTasksRequest, { publish_request_id: id, limit: "1000" })));

        yield all(configCiTestTasks.map(testTask => put(getTestCases(testTask))));

        let testTasks = configCiTestTasks.reduce((acc, item) => ({ ...acc, ...item }), {});

        yield put(setTestTasks(testTasks));
        yield put(getPublishRequestSuccess({
            publishRequests: data,
            count
        }));
    } catch (error) {
        yield put(getPublishRequestError());
        getErrorMessage(error);
    }
}

function* handleConfigCiTestCaseTaskCancel ({ payload }) {
    try {
        yield call(configCiTestCaseTaskCancelRequest, payload);
        yield call(toast.success, `The Test Case with id - ${payload.config_ci_test_task_id} has been successfully canceled`);
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleAcceptPublishRequest ({ payload }) {
    try {
        yield put(mergeAcceptPublishRequestDialog({ isLoading: true }));

        yield call(acceptPublishRequestRequest, payload);
        yield call(toast.success, `The Publish Request with id - ${payload.id} has been successfully accepted`);

        yield put(setAcceptPublishRequestDialog());
        yield handleRefreshPublishRequests();
    } catch (error) {
        yield put(mergeAcceptPublishRequestDialog({ isLoading: false }));
        getErrorMessage(error);
    } finally {

    }
}

function* handleRejectPublishRequest ({ payload }) {
    try {
        const { publishRequests } = yield select(publishRequestsSelector);
        const currentPublishRequest = publishRequests.find(item => item.id === payload);

        yield call(publishRequestRejectRequest, currentPublishRequest);
        yield call(toast.success, `The Publish Request with id - ${payload.id} has been successfully rejected`);
        yield handleRefreshPublishRequests();
    } catch (error) {
        getErrorMessage(error);
    } finally {

    }
}

// Watchers
export function* publishRequestSagas() {
    yield all([
        yield takeLatest(getPublishRequest, handleGetPublishRequest),
        yield takeEvery(getTestCases, handleGetTestCasesData),
        yield takeEvery(configCiTestCaseTaskCancel, handleConfigCiTestCaseTaskCancel),
        yield takeEvery(acceptPublishRequest, handleAcceptPublishRequest),
        yield takeEvery(rejectPublishRequest, handleRejectPublishRequest)
    ]);
}
